import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { ConventionalUsDocumentModel } from "../../../domain/ndt/conventional-us-report-domain.js";
import { usJointSvg } from "../../../domain/ndt/us-joint-types.js";
import {
  GRID_LAYOUT,
  KIT_COLORS,
  KIT_PAGE_MARGINS,
  LAUDO_FILL,
  USABLE_WIDTH,
  buildCroquiBox,
  buildFooter,
  buildHeader,
  buildIdentification,
  buildPhotoBlocks,
  buildSignatories,
  buildTextBox,
  kitStyles,
  pageBreakBefore,
  pairGrid,
  withDocumentVisual,
  sectionBand,
  th,
  value
} from "./ndt-report-kit.js";

/** Largura de cada figura de junta na matriz 2 × 2 (duas por linha na largura útil). */
const JOINT_SVG_WIDTH = 200;

/**
 * RELATÓRIO DE ENSAIO POR ULTRASSOM CONVENCIONAL — FORM-USC REV.00.
 * Seção 04 imprime os QUATRO tipos de junta (como o modelo), marcando os
 * selecionados com ☒ — legível em preto e branco. A figura é o mesmo SVG da
 * página de elaboração, vetorial.
 */
export class UscBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    // O visual do Studio congelado no modelo vale durante toda a montagem (e só nela).
    return withDocumentVisual(((snapshot.data || {}) as ConventionalUsDocumentModel).visual ?? null, () => this.compose(snapshot));
  }

  private compose(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model = (snapshot.data || {}) as ConventionalUsDocumentModel;
    return {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: KIT_PAGE_MARGINS,
      header: () => buildHeader(model, "RELATÓRIO DE ENSAIO POR\nULTRASSOM CONVENCIONAL", "USC"),
      pageBreakBefore,
      footer: buildFooter(model, "FORM-USC", "Relatório de Ensaio por Ultrassom Convencional"),
      content: [
        sectionBand("01", "IDENTIFICAÇÃO"),
        buildIdentification(model),
        sectionBand("02", "DADOS DO ENSAIO"),
        this.buildTestData(model),
        sectionBand("03", "APARELHO, TRANSDUTORES E CALIBRAÇÃO"),
        this.buildEquipment(model),
        this.buildTransducers(model),
        sectionBand("04", "TIPOS DE JUNTAS ENSAIADAS"),
        this.buildJointTypes(model),
        sectionBand("05", "REGISTRO DA INSPEÇÃO / RESULTADOS"),
        this.buildResults(model),
        sectionBand("06", "CROQUI"),
        buildCroquiBox(model.croqui),
        sectionBand("07", "OBSERVAÇÕES E PARECER FINAL"),
        buildTextBox(model.observations),
        sectionBand("08", "EVIDÊNCIAS FOTOGRÁFICAS"),
        buildPhotoBlocks(model.photos),
        sectionBand("09", "TERMO DE RESPONSABILIDADE & SIGNATÁRIOS"),
        buildSignatories(model)
      ],
      styles: kitStyles(),
      defaultStyle: { fontSize: 7.5, color: KIT_COLORS.text }
    };
  }

  private buildTestData(model: ConventionalUsDocumentModel): Content {
    const t = model.testData || ({} as ConventionalUsDocumentModel["testData"]);
    return pairGrid(
      [
        ["Procedimento / Revisão:", t.procedure],
        ["Critério de Aceitação:", t.acceptanceCriteria],
        ["Condição Superficial:", t.surfaceCondition],
        ["Técnica:", t.technique]
      ],
      [
        ["Norma de Referência:", t.referenceNorm],
        ["Material Base:", t.baseMaterial],
        ["Temperatura do Ensaio:", t.testTemperature],
        ["Acoplante:", t.couplant]
      ]
    );
  }

  private buildEquipment(model: ConventionalUsDocumentModel): Content {
    const e = model.equipment || ({} as ConventionalUsDocumentModel["equipment"]);
    return pairGrid(
      [["Aparelho:", e.device], ["Bloco de Calibração:", e.calibrationBlock], ["Verificação / Referência:", e.verificationReference]],
      [["Cert. de Calibração:", e.deviceCertificate], ["Cert. do Bloco:", e.calibrationBlockCertificate], ["Observação:", e.note]]
    );
  }

  /** Nº · TIPO · FREQUÊNCIA (MHz) · ÂNGULO · DIMENSÃO · Nº DE SÉRIE. */
  private buildTransducers(model: ConventionalUsDocumentModel): Content {
    const list = Array.isArray(model.transducers) ? model.transducers : [];
    const body: TableCell[][] = [[th("Nº", "center"), th("Tipo"), th("Frequência (MHz)", "center"), th("Ângulo", "center"), th("Dimensão", "center"), th("Nº de Série")]];
    if (list.length === 0) {
      body.push([{ text: "—", style: "empty", colSpan: 6, alignment: "center" }, {}, {}, {}, {}, {}]);
    } else {
      for (const t of list) {
        body.push([
          { text: t.number, style: "td", alignment: "center" },
          { text: value(t.type), style: "td" },
          { text: value(t.frequency), style: "td", alignment: "center" },
          { text: value(t.angle), style: "td", alignment: "center" },
          { text: value(t.dimension), style: "td", alignment: "center" },
          { text: value(t.serialNumber), style: "td" }
        ]);
      }
    }
    return { margin: [0, 6, 0, 0], table: { headerRows: 1, widths: [26, 120, 80, 60, 80, "*"], body, dontBreakRows: true }, layout: GRID_LAYOUT };
  }

  /** ☒ / ☐ + TIPO n + figura vetorial — os quatro sempre, dois por linha, como o FORM-USC. */
  private buildJointTypes(model: ConventionalUsDocumentModel): Content {
    const list = Array.isArray(model.jointTypes) ? model.jointTypes : [];
    const cell = (j: ConventionalUsDocumentModel["jointTypes"][number]): TableCell => ({
      stack: [
        {
          columns: [
            { width: 12, canvas: this.checkbox(j.selected) },
            { width: "*", text: j.code, style: "jointTitle", bold: true, margin: [4, 0, 0, 0] }
          ],
          margin: [0, 0, 0, 4]
        },
        // A figura de 200 × 120 do domínio; `svg` do modelo quando presente, senão regenerada da mesma definição.
        { svg: j.svg || usJointSvg(j.id, { stroke: "#000000", strokeWidth: 2 }), width: JOINT_SVG_WIDTH, alignment: "center" },
        { text: j.selected ? "SELECIONADO NESTE RELATÓRIO" : " ", style: "label", alignment: "center", margin: [0, 3, 0, 0], bold: j.selected }
      ],
      margin: [0, 2, 0, 2]
    });
    const rows: TableCell[][] = [];
    for (let i = 0; i < list.length; i += 2) {
      const a = list[i];
      const b = list[i + 1];
      rows.push([a ? cell(a) : { text: "" }, b ? cell(b) : { text: "" }]);
    }
    if (rows.length === 0) rows.push([{ text: "—", style: "empty", colSpan: 2, alignment: "center" }, {}]);
    return {
      table: { widths: [USABLE_WIDTH / 2 - 1, "*"], body: rows, dontBreakRows: true },
      layout: GRID_LAYOUT
    };
  }

  private checkbox(checked: boolean): any[] {
    const items: any[] = [{ type: "rect", x: 0, y: 0, w: 9, h: 9, lineWidth: 0.8, lineColor: "#000000" }];
    if (checked) {
      items.push({ type: "line", x1: 1.5, y1: 1.5, x2: 7.5, y2: 7.5, lineWidth: 1.2, lineColor: "#000000" });
      items.push({ type: "line", x1: 7.5, y1: 1.5, x2: 1.5, y2: 7.5, lineWidth: 1.2, lineColor: "#000000" });
    }
    return items;
  }

  /** Nº · JUNTA/REGIÃO · TIPO DE JUNTA · PONTO DE INSPEÇÃO · LOCALIZAÇÃO · INDICAÇÃO/DESCONTINUIDADE · DIMENSIONAMENTO · LAUDO. */
  private buildResults(model: ConventionalUsDocumentModel): Content {
    const rows = Array.isArray(model.results) ? model.results : [];
    const body: TableCell[][] = [[
      th("Nº", "center"), th("Junta / Região"), th("Tipo de Junta", "center"), th("Ponto de Inspeção", "center"),
      th("Localização"), th("Indicação / Descontinuidade"), th("Dimensionamento", "center"), th("Laudo", "center")
    ]];
    if (rows.length === 0) {
      body.push([{ text: "Nenhum item registrado.", style: "empty", colSpan: 8, alignment: "center" }, {}, {}, {}, {}, {}, {}, {}]);
    } else {
      for (const r of rows) {
        const paint = LAUDO_FILL[r.laudo] ?? null;
        body.push([
          { text: value(r.item), style: "td", alignment: "center" },
          { text: value(r.jointRegion), style: "td" },
          { text: value(r.jointType), style: "td", alignment: "center" },
          { text: value(r.inspectionPoint), style: "td", alignment: "center" },
          { text: value(r.location), style: "td" },
          { text: value(r.indication), style: "td" },
          { text: value(r.sizing), style: "td", alignment: "center" },
          paint ? { text: value(r.laudoLabel), style: "td", alignment: "center", bold: true, fillColor: paint.fill, color: paint.color } : { text: value(r.laudoLabel), style: "td", alignment: "center" }
        ]);
      }
    }
    return { table: { headerRows: 1, widths: [22, 70, 42, 40, 60, "*", 62, 62], body, dontBreakRows: true }, layout: GRID_LAYOUT };
  }
}

documentEngine.registerBuilder("USC", new UscBuilder());
